import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { SettingsModal } from '@os/components/SettingsModal';
import { useOsRoot } from '@/lib/theme';

const IFRAME_ROUTES = [
  '/dashboard/kol',
  '/dashboard/medical-watch',
  '/dashboard/personas',
  '/dashboard/market-access',
  '/dashboard/pharmageo',
];

const FULL_HEIGHT_ROUTES_PREFIX = ['/agent', '/agents', '/spaces', '/history'];

/**
 * Authenticated shell for billing, settings and the embedded product apps.
 *
 * `os-scope` is what flips it onto the aikka brand system: this file and its
 * Sidebar/Topbar already style themselves with the semantic tokens
 * (`bg-card`, `border-border`, `text-foreground`), and those became var-based
 * when the OS design system landed — so the scope alone re-themes the shell
 * with no rewrite. `os-root` paints the document element so overscroll and the
 * mobile URL bar do not flash the old light blue.
 *
 * The five product routes embed external apps that keep their own light theme;
 * the frame around them matching the rest of aikka is still more coherent than
 * a light frame between a black landing and a black agent.
 *
 * Responsiveness: the sidebar is a pinned 260px column from `lg` up, and an
 * off-canvas drawer below it. The content column drops its left padding on
 * small screens (`lg:pl-[260px]`) so nothing sits under a hidden sidebar, and a
 * toggle is always reachable — inside the Topbar where one is shown, and as a
 * floating button on the Topbar-less embedded/agent routes.
 */
export function DashboardLayout() {
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Settings is the unified SettingsModal now — the legacy shell opens it in
  // place rather than dead-redirecting to /agent via the retired
  // /dashboard/settings route (parité claude.ai: settings is a pop-up).
  const [settingsOpen, setSettingsOpen] = useState(false);

  const isIframeRoute = IFRAME_ROUTES.includes(location.pathname);
  const isFullHeight = FULL_HEIGHT_ROUTES_PREFIX.some((p) => location.pathname.startsWith(p));
  const showTopbar = !isIframeRoute && !isFullHeight;

  // Tapping a nav item changes the route; closing the drawer on that change is
  // what dismisses it after navigation. Desktop keeps the sidebar pinned
  // regardless of this flag, so it is a no-op there.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer, as expected of any modal-style mobile overlay.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  // Sol du document, compté dans `ThemeProvider` — voir `useOsRoot`.
  useOsRoot();

  return (
    <div
      className="os-scope min-h-screen bg-background"
      // Masqué pour Contentsquare comme `OsShell` : c'est l'autre coque du
      // produit authentifié. Voir `layout/ContentsquareProvider.tsx`.
      data-cs-mask
    >
      <Sidebar
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        onOpenSettings={() => setSettingsOpen(true)}
      />
      {/* `100dvh` et non `100vh` : sur Safari mobile, `100vh` vaut la hauteur
          SANS la barre d'URL, donc le cadre dépasse le bas de l'écran tant que
          la barre est déployée. C'était le dernier `100vh` du dépôt — le reste
          de l'application est déjà en `dvh`/`svh` (OsShell, SharedRunPage,
          style.css). */}
      <div className="flex flex-col lg:pl-[260px]" style={{ minHeight: '100dvh' }}>
        {showTopbar && (
          <Topbar
            onMenuClick={() => setMobileNavOpen(true)}
            onOpenSettings={() => setSettingsOpen(true)}
          />
        )}
        {/* Routes that hide the Topbar (embedded apps, full-height agent) still
            need a way to reach the sidebar on mobile — a floating toggle keeps
            the drawer reachable without stealing height from the frame. */}
        {!showTopbar && (
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label={t('shell.openMenu')}
            className="fixed left-2 top-2 z-30 flex h-9 w-9 items-center justify-center rounded-control border border-border bg-card/90 text-foreground shadow-sm backdrop-blur lg:hidden"
          >
            <Menu className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {isIframeRoute || isFullHeight ? (
          <div className="flex-1">
            <Outlet />
          </div>
        ) : (
          <main className="relative z-10 flex-1 p-6 bg-background">
            <Outlet />
          </main>
        )}
      </div>
      {/* Unified settings surface, opened in place from the legacy Topbar/Sidebar
          so those controls no longer dead-redirect to /agent. The modal portals
          to document.body but re-scopes itself, so it renders on the aikka DA. */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
